import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setUserPosition, setUserRole, unSetUserPosotion, unSetUserRole } from 'redux/companiesUsers-service/actions';
// types
import { TABLE_HEADERS, TABLE_ITEMS_CLASSNAMES } from 'core/constants';
import { USER_ROLES } from '@joc/api-gateway';
// components
import PopupContainer from 'shared/components/PopupContainer';
import WhiteContainer from 'shared/components/WhiteContainer';
import TableMain from 'shared/components/Table/TableMain';
import TableMenu from 'components/Organization/TableMenu';
import TableHead from 'shared/components/Table/TableHead';
import TableBody from 'shared/components/Table/TableBody';
import TableRow from 'shared/components/Table/TableRow';
import Loader from 'shared/components/Loader';
import TableHeadCell from 'shared/components/Table/TableHead/TableHeadCell';
import Checkbox from 'shared/components/Checkbox';
import FullNameCell from 'shared/components/Table/CellRenderers/FullNameCell';
import ButtonDefault from 'shared/components/Buttons/ButtonsDefault';
import CreatePositionPopup from '../CreatePositionPopup';
import PopupConfirmation from 'shared/components/PopupConfirmation';

// helpers
import { getAllUserRolesInEachOrg, getSortedUserOrgsList } from './helpers';
// context
import { ISortedRolesItem, UserTableDataContext } from '../../UsersTable';
// styles
import styles from './EditRolePopup.module.scss';

type EditRolePopupProps = {
	setIsShowPopup: () => void;
};

const EditRolePopup: FC<EditRolePopupProps> = ({ setIsShowPopup }) => {
	const { isNeedCloseModal, setIsNeedCloseModal, userData } = useContext(UserTableDataContext);
	const [userToDelete, setUserToDelete] = useState<{
		userId: number;
		orgId: string;
		value: number;
		message: string;
		callback: () => void;
	} | null>(null);

	const dispatch = useDispatch();

	const [isShowCreatePositionPopup, setIsShowCreatePositionPopup] = useState(false);
	const [isActionLoading, setIsActionLoading] = useState(false);

	const [search, setSearch] = useState('');

	const userRolesInEachOrg = useMemo(() => getAllUserRolesInEachOrg(userData.roles), [userData.roles]);
	const sortedUserOrgsList = useMemo(() => getSortedUserOrgsList(userRolesInEachOrg, true), [userRolesInEachOrg]);

	useEffect(() => {
		if (isNeedCloseModal) {
			setIsShowPopup();
			if (setIsNeedCloseModal) {
				setIsNeedCloseModal(false);
			}
		}
	}, [isNeedCloseModal]);

	const changeUserRole = useCallback(
		async (checked: boolean, value?: number, orgId?: string) => {
			if (value && orgId && !isActionLoading) {
				const userRoleDetails = sortedUserOrgsList.find(user => user.organizationId === orgId);

				setIsActionLoading(true);
				try {
					if (checked) {
						await dispatch(setUserRole(+userData.id, orgId, value));
					} else if (
						(userRoleDetails && userRoleDetails.roleList.length && userRoleDetails.roleList.length > 1) ||
						sortedUserOrgsList.length > 1
					) {
						await dispatch(unSetUserRole(+userData.id, orgId, value));
					} else {
						setUserToDelete({
							userId: +userData.id,
							orgId,
							value,
							message: 'The user without roles will be deleted.',
							callback: async () => {
								setIsActionLoading(true);
								try {
									await dispatch(unSetUserRole(+userData.id, orgId, value));
									if (setIsNeedCloseModal) setIsNeedCloseModal(true);
									refresh();
								} finally {
									setIsActionLoading(false);
								}
							},
						});
					}
				} finally {
					setIsActionLoading(false);
				}
			}
		},
		[sortedUserOrgsList, isActionLoading, userData.id, setIsNeedCloseModal]
	);

	const changeUserPosition = useCallback(
		async (checked: boolean, value?: number, orgId?: string) => {
			if (value && orgId && !isActionLoading) {
				const userRoleDetails = sortedUserOrgsList.find(user => user.organizationId === orgId);

				setIsActionLoading(true);
				try {
					if (checked) {
						await dispatch(setUserPosition(+userData.id, orgId, value));
					} else if (
						userRoleDetails &&
						userRoleDetails.roleList.length &&
						userRoleDetails.roleList.length >= 1
					) {
						if (
							userRoleDetails.roleList.some(role => role.roleName === 'VOLUNTEER') ||
							sortedUserOrgsList.length > 1
						) {
							await dispatch(unSetUserPosotion(+userData.id, orgId, value));
						} else {
							setUserToDelete({
								userId: +userData.id,
								orgId,
								value,
								message: 'The user without a Volunteer role or any positions will be deleted.',
								callback: async () => {
									setIsActionLoading(true);
									try {
										await dispatch(unSetUserPosotion(+userData.id, orgId, value));
										if (setIsNeedCloseModal) setIsNeedCloseModal(true);
										refresh();
									} finally {
										setIsActionLoading(false);
									}
								},
							});
						}
					} else {
						setUserToDelete({
							userId: +userData.id,
							orgId,
							value,
							message: 'The user without positions will be deleted.',
							callback: async () => {
								setIsActionLoading(true);
								try {
									await dispatch(unSetUserPosotion(+userData.id, orgId, value));
									if (setIsNeedCloseModal) setIsNeedCloseModal(true);
									refresh();
								} finally {
									setIsActionLoading(false);
								}
							},
						});
					}
				} finally {
					setIsActionLoading(false);
				}
			}
		},
		[sortedUserOrgsList, isActionLoading, userData.id, setIsNeedCloseModal]
	);

	const refresh = useCallback(() => {
		setUserToDelete(null);
	}, [sortedUserOrgsList]);

	const getIsDefaultRoleChecked = (organization: ISortedRolesItem, role: USER_ROLES) =>
		organization.roleList.some(({ roleName }) => roleName === role);
	const getIsOwner = (organization: ISortedRolesItem) =>
		userData.userOrganizations?.some(
			({ organizationId, position }) =>
				organizationId === organization.organizationId && position?.organizationRoleName === 'Owner'
		);

	const showCreatePositionPopupHandler = () => {
		setIsShowCreatePositionPopup(prev => !prev);
	};

	const changeSearchHandler = (value = '') => setSearch(value);

	const roles = useMemo(
		() =>
			sortedUserOrgsList.filter(({ organization, roleList }) => {
				if (!organization && !roleList.length) return false;
				if (organization) {
					const { organizationName } = organization;

					return organizationName.toLowerCase().includes(search.toLowerCase());
				}
				return 'private'.includes(search.toLowerCase());
			}),
		[search, sortedUserOrgsList]
	);
	return (
		<PopupContainer setIsShowPopup={setIsShowPopup}>
			<WhiteContainer
				title="Role in organization"
				subtitle={`${userData.firstName ?? ''} ${userData.lastName ?? ''}`}
				parentClassName={styles.whiteContainer}
			>
				<TableMenu searchChangeHandler={changeSearchHandler} />
				<div className={styles.tableWrapper}>
					{isActionLoading && (
						<div className={styles.overlayLoader}>
							<div className={styles.loaderSpinner}>
								<Loader />
							</div>
						</div>
					)}
					<TableMain>
						{roles.length ? (
							<TableHead className={styles.table__header} callChild="roles">
								<TableHeadCell
									itemClassName={TABLE_ITEMS_CLASSNAMES.fullName}
									text={TABLE_HEADERS.organizations}
									isSortDisabled={true}
								/>
								<TableHeadCell
									itemClassName={TABLE_ITEMS_CLASSNAMES.coordinator}
									text={TABLE_HEADERS.volunteerRole}
									isSortDisabled={true}
								/>
								<TableHeadCell
									itemClassName={TABLE_ITEMS_CLASSNAMES.coordinator}
									text={TABLE_HEADERS.admin}
									isSortDisabled={true}
								/>
								<TableHeadCell
									itemClassName={TABLE_ITEMS_CLASSNAMES.coordinator}
									text={TABLE_HEADERS.marketer}
									isSortDisabled={true}
								/>
								<TableHeadCell
									itemClassName={TABLE_ITEMS_CLASSNAMES.coordinator}
									text={TABLE_HEADERS.coordinator}
									isSortDisabled={true}
								/>
								<TableHeadCell
									itemClassName={TABLE_ITEMS_CLASSNAMES.coordinator}
									text={TABLE_HEADERS.owner}
									isSortDisabled={true}
								/>
							</TableHead>
						) : null}
						{!userData.roles ? (
							<div className={styles.loader}>
								<Loader />
							</div>
						) : (
							<TableBody>
								{roles.map((organization, index) => (
									<TableRow key={index} callChild="roles">
										<FullNameCell
											firstName={organization.organization?.organizationName || 'Private'}
											lastName=""
											isUnsetMaxWidth={true}
											crop={true}
										/>
										<div className={styles.checkboxWrapper}>
											<Checkbox
												defaultChecked={getIsDefaultRoleChecked(
													organization,
													USER_ROLES.VOLUNTEER
												)}
												value={3}
												changeHandle={(checked, value) => {
													changeUserRole(checked, value, organization.organizationId);
												}}
											/>
										</div>
										<div className={styles.checkboxWrapper}>
											<Checkbox
												defaultChecked={getIsDefaultRoleChecked(organization, USER_ROLES.ADMIN)}
												value={4}
												changeHandle={(checked, value) =>
													changeUserRole(checked, value, organization.organizationId)
												}
												disabled={!organization.organization?.organizationName}
											/>
										</div>
										<div className={styles.checkboxWrapper}>
											<Checkbox
												defaultChecked={getIsDefaultRoleChecked(
													organization,
													USER_ROLES.MARKETER
												)}
												value={5}
												changeHandle={(checked, value) =>
													changeUserRole(checked, value, organization.organizationId)
												}
												disabled={!organization.organization?.organizationName}
											/>
										</div>
										<div className={styles.checkboxWrapper}>
											<Checkbox
												defaultChecked={getIsDefaultRoleChecked(
													organization,
													USER_ROLES.COORDINATOR
												)}
												value={6}
												changeHandle={(checked, value) =>
													changeUserRole(checked, value, organization.organizationId)
												}
												disabled={!organization.organization?.organizationName}
											/>
										</div>
										<div className={styles.checkboxWrapper}>
											<Checkbox
												defaultChecked={getIsOwner(organization)}
												value={1}
												changeHandle={(checked, value) =>
													changeUserPosition(checked, value, organization.organizationId)
												}
												tooltip={
													getIsOwner(organization)
														? 'The Owner can only be reassigned to another User and cannot be removed'
														: undefined
												}
												disabled={getIsOwner(organization)}
											/>
										</div>
									</TableRow>
								))}
							</TableBody>
						)}
						{!roles.length ? (
							<div className={styles.noItemsContainer}>
								<div className={styles.noItemsContainer__gradient} />
								<div className={styles.noItemsTextContainer}>
									<span className={styles.noItemsTitle}>Oops</span>
									{!sortedUserOrgsList.length ? (
										<span className={styles.noItemsText}>
											There are currently no roles in the list.
										</span>
									) : (
										<span className={styles.noItemsText}>
											No roles found for{' '}
											<b>
												{'"'}
												{search}
												{'"'}
											</b>
										</span>
									)}
								</div>
							</div>
						) : null}
					</TableMain>
				</div>
			</WhiteContainer>
			<div className={styles.buttons_block}>
				<ButtonDefault
					title="+Add position"
					classList="secondary_active, lg"
					clickHandler={showCreatePositionPopupHandler}
					disabled={isActionLoading}
				/>
			</div>
			{isShowCreatePositionPopup && (
				<CreatePositionPopup setIsShowPopup={showCreatePositionPopupHandler} userId={+userData.id} />
			)}
			{userToDelete && (
				<PopupConfirmation
					confirmClickHandler={() => userToDelete?.callback()}
					subtitle={userToDelete.message}
					cancelClickHandler={() => setUserToDelete(null)}
					setIsShowPopup={() => setUserToDelete(null)}
				/>
			)}
		</PopupContainer>
	);
};

export default EditRolePopup;
